// v4 marmot — REDESIGNED. A plump alpine marmot sitting up: grounded
// haunches under an upright torso with a pale belly, a round head with a
// pale muzzle, dark nose and buck teeth, full cheeks and low round ears,
// little arms held in front with dark paws, flat grounded feet, and a thick
// tail trailing behind. Two coats: golden-brown and grizzled grey.
//
// Ported 1:1 from age-of-mcp's v4/marmot.ts, using the library's shared
// `wildland.eyes`/`wildland.tube` helpers — identical to the v4 source's.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const { eyes, tube } = wildland;

const COATS = [
  { fur: "#8e6c40", belly: "#d9b988", dark: "#66482a", tail: "#4e3820" },
  { fur: "#8d8678", belly: "#cfc6b2", dark: "#5c564a", tail: "#463f34" },
];

function partsFor(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = [
    // grounded haunches + upright plump torso + pale belly
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.fur,
      position: [-0.004, 0.034, 0], scale: [1.15, 1.0, 1.15] },
    { geometry: new THREE.IcosahedronGeometry(0.028, 0), color: c.fur,
      position: [0.004, 0.066, 0], scale: [0.95, 1.25, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.belly,
      position: [0.018, 0.06, 0], scale: [0.7, 1.15, 0.75] },
    // round head, pale muzzle, dark nose, buck teeth
    { geometry: new THREE.IcosahedronGeometry(0.0205, 0), color: c.fur,
      position: [0.012, 0.104, 0], scale: [1.1, 0.95, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.008, 0), color: c.belly,
      position: [0.032, 0.1, 0], scale: [1.1, 0.8, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: "#2a1c10",
      position: [0.04, 0.101, 0] },
    { geometry: new THREE.BoxGeometry(0.003, 0.005, 0.005), color: "#f2e8d0",
      position: [0.0335, 0.092, 0] },
    // thick tail trailing on the ground behind
    tube([-0.034, 0.025, 0], [-0.052, 0.012, 0.004], 0.007, 0.009, c.dark),
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.tail,
      position: [-0.058, 0.01, 0.006] },
  ];
  for (const s of [1, -1] as const) {
    parts.push(
      // full cheeks, low round ears
      { geometry: new THREE.IcosahedronGeometry(0.0085, 0), color: c.belly,
        position: [0.022, 0.098, s * 0.013] },
      { geometry: new THREE.IcosahedronGeometry(0.0055, 0), color: c.dark,
        position: [0.006, 0.121, s * 0.0165] },
      // little arms held in front, dark paws, flat grounded feet
      tube([0.018, 0.082, s * 0.012], [0.03, 0.062, s * 0.0095],
        0.0045, 0.0035, c.fur),
      { geometry: new THREE.IcosahedronGeometry(0.0045, 0), color: c.dark,
        position: [0.032, 0.059, s * 0.009] },
      { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.dark,
        position: [0.018, 0.015, s * 0.017], scale: [1.5, 0.6, 0.95] },
    );
  }
  parts.push(...eyes(0.024, 0.11, 0.0105, 0.0035, "#1a120a"));
  return parts;
}

export const marmotV4 = defineModel({
  id: "marmot-v4",
  batchId: "polished",
  name: "Marmot (v4)",
  tags: ["creature","marmot","low-poly","age-of-mcp","fable"],
  summary: "Low-poly marmot.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.07,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
  ],
  animations: [],
});
